import React, { useEffect, useState } from "react";
import Card from "components/card";
import { getEmployees } from "services/hrms.api";
import { useNavigate } from "react-router-dom";

const DEPT_COLORS = {
  Finance: "bg-sky-100 text-sky-700",
  Development: "bg-rose-100 text-rose-700",
  Marketing: "bg-blue-100 text-blue-700",
  Manager: "bg-fuchsia-100 text-fuchsia-700",
  "UI/UX Design": "bg-pink-100 text-pink-700",
  Operations: "bg-emerald-100 text-emerald-700",
  HR: "bg-orange-100 text-orange-700",
  Default: "bg-gray-100 text-gray-700",
};

export default function EmployeesCard() {
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        setError("");
        setLoading(true);

        const { data } = await getEmployees();

        const mapped = (data || []).map((emp) => ({
          id: emp.id || emp._id,
          name: joinName(emp.firstName, emp.lastName, emp.code),
          role: emp.designation || emp.role || "—",
          dept: emp.department || "Unknown",
          avatar: emp.avatar || null,
        }));

        setRows(mapped);
      } catch (err) {
        console.error(err);
        setError("Failed to load employees.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // 🔹 show only first 6 employees in the card
  const visibleRows = rows.slice(0, 6);

  const handleViewAll = () => {
    navigate("/admin/hrms/employees");
  };

  return (
    <Card extra="w-full rounded-3xl p-0 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3 dark:border-white/10">
        <h4 className="text-base font-semibold text-navy-700 dark:text-white">
          Employees
        </h4>
        <button
          onClick={handleViewAll}
          className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-white/10 dark:text-white/80 dark:hover:bg-white/10"
        >
          View All
        </button>
      </div>

      {/* Loading */}
      {loading && (
        <div className="px-5 py-4 text-xs text-gray-500 dark:text-white/70">
          Loading employees...
        </div>
      )}

      {/* Error */}
      {error && !loading && (
        <div className="text-rose-500 dark:text-rose-300 px-5 py-4 text-xs">
          {error}
        </div>
      )}

      {/* Table header */}
      {!loading && !error && (
        <div className="grid grid-cols-12 px-5 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400">
          <div className="col-span-8">Name</div>
          <div className="col-span-4">Department</div>
        </div>
      )}

      {/* Rows */}
      <div className="divide-y divide-gray-100 dark:divide-white/10">
        {visibleRows.map((r) => (
          <div key={r.id} className="grid grid-cols-12 items-center px-5 py-4">
            {/* Name + role */}
            <div className="col-span-8">
              <div className="flex items-center gap-3">
                {r.avatar ? (
                  <img
                    src={r.avatar}
                    alt={r.name}
                    className="h-10 w-10 rounded-full object-cover"
                  />
                ) : (
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-200 text-sm font-semibold text-gray-600">
                    {getInitials(r.name)}
                  </div>
                )}

                <div>
                  <div className="text-sm font-semibold text-navy-700 dark:text-white">
                    {r.name}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {r.role}
                  </div>
                </div>
              </div>
            </div>

            {/* Department pill */}
            <div className="col-span-4">
              <span
                className={`inline-block rounded-md px-2 py-1 text-[11px] font-semibold ${
                  DEPT_COLORS[r.dept] || DEPT_COLORS.Default
                }`}
              >
                {r.dept}
              </span>
            </div>
          </div>
        ))}

        {/* No data */}
        {!loading && visibleRows.length === 0 && !error && (
          <div className="px-5 py-4 text-xs text-gray-500 dark:text-gray-400">
            No employees found.
          </div>
        )}
      </div>
    </Card>
  );
}

/* ---------------- helpers ---------------- */

function joinName(first, last, fallback) {
  const f = (first || "").trim();
  const l = (last || "").trim();
  const full = `${f} ${l}`.trim();
  return full || fallback || "Unnamed";
}

function getInitials(name = "") {
  const parts = name.trim().split(" ");
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0][0]?.toUpperCase() || "?";
  return (parts[0][0] + parts[1][0]).toUpperCase();
}
